import type { TrainingPlanDto, TrainingPlanSummaryDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { draftFromPlan, draftToInput, emptyDraft, TRAINING_PLANS_RESOURCE, type PlanDraft } from '../lib/training';
import { PageHeader } from '../components/PageHeader';
import { LinkButton, TestBadge } from '../components/training/common';
import { OfflineSaveButton } from '../components/training/OfflineSaveButton';
import { PlanEditor } from '../components/training/PlanEditor';
import { PlanView } from '../components/training/PlanView';
import { PlanImageButton } from '../components/training/ShareImageButton';
import { Button, Card, CardBody, Dialog, EmptyState, ErrorBlock, LoadingBlock, useToast } from '../components/ui';

/**
 * Vorlagen fuer Trainingseinheiten.
 *
 * Eine Vorlage ist eine Einheit ohne Termin. Sie wird beim Uebernehmen in ein
 * Training kopiert - spaetere Aenderungen an Vorlage und Training beruehren
 * sich nicht.
 */
export function TrainingPlansPage(): JSX.Element {
  const { can } = useAuth();
  const canEdit = can(TRAINING_PLANS_RESOURCE, 'update');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [editor, setEditor] = useState<{ id: string | null; draft: PlanDraft } | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['training-templates'],
    queryFn: () => api.get<TrainingPlanSummaryDto[]>('/api/training/plans/templates'),
  });
  const viewed = useQuery({
    queryKey: ['training-plan', viewId],
    queryFn: () => api.get<TrainingPlanDto>(`/api/training/plans/${viewId}`),
    enabled: viewId !== null,
  });

  const fail = (fallback: string) => (error: unknown) => toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const save = useMutation({
    mutationFn: ({ id, draft }: { id: string | null; draft: PlanDraft }) => {
      const input = draftToInput(draft);
      return id
        ? api.put<TrainingPlanDto>(`/api/training/plans/${id}`, input)
        : api.post<TrainingPlanDto>('/api/training/plans', { ...input, teamId: null });
    },
    onSuccess: async (_saved, { id }) => {
      await queryClient.invalidateQueries({ queryKey: ['training-templates'] });
      await queryClient.invalidateQueries({ queryKey: ['training-plan'] });
      setEditor(null);
      toast.show(id ? 'Vorlage gespeichert' : 'Vorlage angelegt');
    },
    // Der Editor bleibt offen und zeigt die Meldung; zusätzlich der Hinweis.
    onError: fail('Speichern fehlgeschlagen'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/training/plans/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['training-templates'] });
      setViewId(null);
      toast.show('Vorlage gelöscht');
    },
    onError: fail('Die Vorlage konnte nicht gelöscht werden'),
  });

  const edit = async (template: TrainingPlanSummaryDto): Promise<void> => {
    setLoadingId(template.id);
    try {
      const plan = await queryClient.fetchQuery({
        queryKey: ['training-plan', template.id],
        queryFn: () => api.get<TrainingPlanDto>(`/api/training/plans/${template.id}`),
        staleTime: 0,
      });
      save.reset();
      setEditor({ id: template.id, draft: draftFromPlan(plan) });
    } catch (error) {
      fail('Die Vorlage konnte nicht geladen werden')(error);
    } finally {
      setLoadingId(null);
    }
  };

  const confirmRemove = (template: { id: string; title: string }): void => {
    if (window.confirm(`Vorlage „${template.title}“ löschen? Trainings, die sie schon übernommen haben, behalten ihre Einheit.`)) remove.mutate(template.id);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Einheiten"
        description="Vorlagen für Trainingseinheiten – für alle Trainer des Vereins"
        scoped={false}
        actions={
          <>
            <span className="self-center"><TestBadge /></span>
            <LinkButton to="/sport/uebungen">Übungen</LinkButton>
            {canEdit && <Button onClick={() => { save.reset(); setEditor({ id: null, draft: emptyDraft() }); }}>+ Vorlage</Button>}
          </>
        }
      />

      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Die Trainingsplanung läuft als Test. Eine Einheit für ein bestimmtes Training planst du bei der Mannschaft im Reiter „Termine“
        – dort kannst du eine Vorlage auch übernehmen oder eine Einheit als Vorlage speichern.
      </p>

      {templates.isLoading && <LoadingBlock />}
      {templates.error && <ErrorBlock error={templates.error} />}
      {templates.data && templates.data.length === 0 && (
        <EmptyState
          title="Noch keine Vorlagen"
          description={canEdit ? 'Lege eine Vorlage an oder speichere eine geplante Einheit als Vorlage.' : undefined}
          action={canEdit ? <Button onClick={() => { save.reset(); setEditor({ id: null, draft: emptyDraft() }); }}>+ Vorlage</Button> : undefined}
        />
      )}
      {templates.data && templates.data.length > 0 && (
        <Card>
          <CardBody>
            <ul className="divide-y divide-slate-100">
              {templates.data.map((template) => (
                <li key={template.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <button type="button" onClick={() => setViewId(template.id)} className="min-w-0 text-left" aria-label={`${template.title} ansehen`}>
                    <span className="block break-words font-medium text-slate-900 hover:text-brand-700">{template.title}</span>
                    <span className="block text-xs text-slate-500">
                      {template.itemCount} {template.itemCount === 1 ? 'Eintrag' : 'Einträge'} · {template.totalMinutes} Min.
                      {template.focus && ` · ${template.focus}`}
                      {template.teamName && ` · ${template.teamName}`}
                      {` · geändert ${formatDate(template.updatedAt)}`}
                    </span>
                  </button>
                  {canEdit && (
                    <span className="flex flex-wrap gap-2">
                      <Button size="sm" variant="secondary" loading={loadingId === template.id} onClick={() => void edit(template)}>
                        Bearbeiten<span className="sr-only"> · {template.title}</span>
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => confirmRemove(template)}>
                        Löschen<span className="sr-only"> · {template.title}</span>
                      </Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {viewId && !editor && (
        <Dialog open onClose={() => setViewId(null)} title={viewed.data?.title ?? 'Vorlage'} wide footer={<Button variant="secondary" onClick={() => setViewId(null)}>Schließen</Button>}>
          {viewed.isLoading && <LoadingBlock />}
          {viewed.error && <ErrorBlock error={viewed.error} />}
          {viewed.data && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <PlanImageButton plan={viewed.data} subtitle={['Vorlage', viewed.data.teamName].filter(Boolean).join(' · ')} />
                <OfflineSaveButton plan={viewed.data} meta={{ kind: 'template', title: viewed.data.title, teamName: viewed.data.teamName }} />
              </div>
              <PlanView plan={viewed.data} />
            </div>
          )}
        </Dialog>
      )}

      {editor && (
        <PlanEditor
          title={editor.id ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
          mode="template"
          initial={editor.draft}
          saving={save.isPending}
          serverError={save.error ? (save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen') : null}
          onSave={(draft) => save.mutate({ id: editor.id, draft })}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}
